import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useForm } from 'react-hook-form'

type Values = {
  query: string
}

type useTokenAddressSearchFormProps = {
  isValidAddress: (address: string) => boolean
  isLoading: boolean
  onSubmit: (address: string) => void
  onBeforeSubmit?: () => void
  minLength?: number
}

export const useTokenAddressSearchForm = ({
  isValidAddress,
  isLoading,
  onSubmit,
  onBeforeSubmit,
  minLength = 3,
}: useTokenAddressSearchFormProps) => {
  const { control, formState, handleSubmit, watch } = useForm<Values>({
    values: { query: '' },
    mode: 'all',
  })

  const query = watch('query')
  const lastSubmittedAddressRef = useRef<string | undefined>(undefined)

  const trackSubmitted = useCallback((address: string) => {
    lastSubmittedAddressRef.current = address
  }, [])

  const handleSubmitInternal = handleSubmit((values) => {
    onBeforeSubmit?.()
    trackSubmitted(values.query)
    onSubmit(values.query)
  })

  const runSubmitRef = useRef(handleSubmitInternal)
  runSubmitRef.current = handleSubmitInternal

  const triggerSubmit = useCallback(() => {
    void runSubmitRef.current()
  }, [])

  useEffect(() => {
    if (!query) {
      lastSubmittedAddressRef.current = undefined
      return
    }

    if (isLoading) {
      return
    }

    if (query.length < minLength || !isValidAddress(query)) {
      return
    }

    if (lastSubmittedAddressRef.current === query) {
      return
    }

    triggerSubmit()
  }, [query, isValidAddress, isLoading, minLength, triggerSubmit])

  const queryFieldRules = useMemo(
    () => ({
      required: true as const,
      minLength,
      validate: (value: string) => {
        if (!isValidAddress(value)) {
          return 'invalid_address' as const
        }
        return undefined
      },
    }),
    [isValidAddress, minLength],
  )

  return {
    control,
    formState,
    handleSubmitInternal,
    queryFieldRules,
  }
}
